import React, { useState } from 'react';
import { FiMenu, FiX, FiShoppingCart, FiInfo } from 'react-icons/fi'; // Added FiInfo icon
import { Link, useLocation } from 'react-router-dom';
import { ABOUT, CONTACT, HOME, PRODUCTS, CART } from '../../lib/routes';
import useLogout from '../../hooks/auth/useLogout';
import { LOGIN } from '../../lib/routes';
import useAuth from '../../hooks/auth/useAuth';
import useCart from '../../hooks/cart/useCart';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(true); // New state for disclaimer
  const { cart } = useCart();
  const { user } = useAuth();
  const { logout, loading } = useLogout();
  const location = useLocation();

  const totalItemsInCart = cart.reduce(
    (total, item) => total + (item.quantity || 1),
    0
  );

  const navLinks = [
    { name: 'Home', to: HOME },
    { name: 'Toys & Figures', to: `${PRODUCTS}?category=toys-figures` },
    { name: 'Trading Cards', to: `${PRODUCTS}?category=trading-cards` },
    { name: 'Accessories', to: `${PRODUCTS}?category=accessories` },
    { name: 'About', to: ABOUT },
    { name: 'Contact', to: CONTACT },
  ];

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Shipping Disclaimer Bar */}
      {showDisclaimer && (
        <div className="bg-purple-600 text-white text-xs sm:text-sm py-2 px-4 flex justify-center items-center relative animate-in slide-in-from-top duration-300">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setShowDisclaimer(false)}>
            <FiInfo className="shrink-0" />
            <p className="font-medium text-center">
              An additional <span className="font-bold">$9.99</span> will be added for shipping to each order. 
              <span className="ml-2 underline opacity-80 hover:opacity-100">Dismiss</span>
            </p>
          </div>
          <button 
            onClick={() => setShowDisclaimer(false)}
            className="absolute right-4 hover:bg-white/20 rounded-full p-1 transition-colors"
          >
            <FiX />
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Bar */}
        <div className="flex justify-between items-center py-4">
          
          {/* Logo */}
          <Link
            to={HOME}
            className="text-2xl font-bold text-gray-900 tracking-tight shrink-0"
          >
            Loot<span className="text-purple-600">Layer</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex space-x-8">
            {navLinks.map((link) => {
              const isActive = location.pathname + location.search === link.to;
              return (
                <Link
                  key={link.name}
                  to={link.to}
                  className={`relative text-sm font-semibold transition-colors duration-200 hover:text-purple-600 ${
                    isActive ? 'text-purple-600' : 'text-gray-700'
                  }`}
                >
                  {link.name}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-purple-600 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Actions: Cart + Auth */}
          <div className="flex items-center space-x-5">
            
            {/* Cart */}
            <Link to={CART} className="relative group p-1">
              <FiShoppingCart className="text-2xl text-gray-700 group-hover:text-purple-600 transition-colors" />
              {totalItemsInCart > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-[10px] font-bold text-white ring-2 ring-white">
                  {totalItemsInCart}
                </span>
              )}
            </Link>

            {/* Auth Button */}
            {user ? (
              <button
                onClick={logout}
                disabled={loading}
                className="hidden sm:block px-4 py-2 text-sm font-medium text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50"
              >
                {loading ? '...' : 'Logout'}
              </button>
            ) : (
              <Link
                to={LOGIN}
                className="hidden sm:block px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Login
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-2xl text-gray-700 hover:text-purple-600 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div className="md:hidden border-t border-gray-100 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.to}
                className="block px-2 py-3 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                onClick={() => setMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}

            {user && (
              <button
                onClick={() => { logout(); setMenuOpen(false); }}
                className="w-full text-left px-2 py-3 text-base font-medium text-red-500 hover:bg-red-50 rounded-lg transition-all"
              >
                Logout
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;